import { SortEnumType } from '../../../generated/graphql';

export type SortDirection = 'asc' | 'desc';

export type TableSort = {
    field: string;
    direction: SortDirection;
};

export type TableSortValue = string | number | boolean | Date | null | undefined;

const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: 'base',
});

export const getNestedValue = (value: unknown, path: string): unknown => {
    if (!path) return undefined;
    return path.split('.').reduce<unknown>((current, part) => {
        if (current === null || current === undefined || typeof current !== 'object') return undefined;
        return (current as Record<string, unknown>)[part];
    }, value);
};

const compareValues = (left: TableSortValue, right: TableSortValue): number => {
    if (left === null || left === undefined) return right === null || right === undefined ? 0 : 1;
    if (right === null || right === undefined) return -1;

    const leftValue = left instanceof Date ? left.getTime() : left;
    const rightValue = right instanceof Date ? right.getTime() : right;

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return leftValue - rightValue;
    }

    if (typeof leftValue === 'boolean' && typeof rightValue === 'boolean') {
        return Number(leftValue) - Number(rightValue);
    }

    return collator.compare(String(leftValue), String(rightValue));
};

export const sortRows = <TRow>(
    rows: readonly TRow[],
    sort: TableSort | null,
    valueFor: (row: TRow, field: string) => TableSortValue,
): TRow[] => {
    if (!sort) return [...rows];

    return rows
        .map((row, index) => ({ row, index }))
        .sort((left, right) => {
            const leftValue = valueFor(left.row, sort.field);
            const rightValue = valueFor(right.row, sort.field);
            const leftMissing = leftValue === null || leftValue === undefined;
            const rightMissing = rightValue === null || rightValue === undefined;
            if (leftMissing !== rightMissing) return leftMissing ? 1 : -1;

            const comparison = compareValues(leftValue, rightValue);
            const directedComparison = sort.direction === 'asc' ? comparison : -comparison;
            return directedComparison || left.index - right.index;
        })
        .map(({ row }) => row);
};

export const nextSort = (current: TableSort | null, field: string): TableSort | null => {
    if (current?.field !== field) return { field, direction: 'asc' };
    if (current.direction === 'asc') return { field, direction: 'desc' };
    return null;
};

/**
 * Produces a deterministic Hot Chocolate order. The ID tie-breaker prevents
 * records with equal primary values moving between cursor pages.
 */
export const createGraphQLOrder = <TSortInput extends object>(
    sort: TableSort | null,
    fallback: TableSort = { field: 'id', direction: 'asc' },
): TSortInput[] => {
    const effectiveSort = sort ?? fallback;
    const direction = effectiveSort.direction === 'asc' ? SortEnumType.Asc : SortEnumType.Desc;
    const order = [{ [effectiveSort.field]: direction }] as TSortInput[];

    if (effectiveSort.field !== 'id') {
        // Keeping both keys in the same direction lets MongoDB satisfy ASC and
        // DESC requests by traversing one compound index in either direction.
        order.push({ id: direction } as TSortInput);
    }

    return order;
};
